/**
 * The Offer Management filter contract, declared once.
 *
 * Each entry ties three things together:
 *
 *   key    the property on the page's `filters` object -- the v-model
 *   param  the request parameter name, or a function for the multi-param ones
 *   kind   what "empty" means, and how the chip renders
 *
 * Declaring the v-model key and the wire name side by side is the entire point.
 * The legacy screens this replaces bound one name and sent another in four
 * separate places, so those filters rendered, accepted input, reported
 * "3 selected", and filtered nothing. Nobody noticed, because a filter that
 * does nothing looks exactly like a filter whose value matches everything.
 *
 * With `toParams()` as the only writer, that failure cannot recur: a filter
 * with no spec entry never reaches the request, and one with a spec entry
 * cannot disagree with itself.
 */
import fmt from "./format";

export const APPLICATION_FILTERS = [
  /* -------------------------------------------------------------- customer */
  { key: "city", label: "City", kind: "multi", param: "city", group: "Customer" },
  { key: "state", label: "State", kind: "multi", param: "state", group: "Customer" },
  {
    key: "lead_partner",
    label: "Lead source",
    kind: "multi",
    param: "lead_partner",
    group: "Customer",
  },
  {
    key: "availed_offer",
    label: "Availed offers",
    kind: "object",
    group: "Customer",
    display: (v) => v.name,
    param: (v) => ({
      availed_offer_count_min: v.min_value,
      availed_offer_count_max: v.max_value,
    }),
  },

  /* ----------------------------------------------------------------- dates */
  {
    key: "applied_range",
    label: "Applied",
    kind: "range",
    group: "Dates",
    param: (v) => ({ loan_from_date: v[0], loan_to_date: v[1] }),
  },
  {
    key: "disbursal_range",
    label: "Disbursed",
    kind: "range",
    group: "Dates",
    param: (v) => ({ disbursal_from_date: v[0], disbursal_to_date: v[1] }),
  },
  {
    key: "follow_up_date",
    label: "Follow-up",
    kind: "date",
    param: "follow_up_date",
    group: "Dates",
  },

  /* ------------------------------------------------------------ processing */
  {
    key: "is_assigned",
    label: "Agent type",
    kind: "bool",
    param: "is_assigned",
    group: "Processing",
    // Clearing "Assigned/Unassigned" hides the agent picker, so the agent it
    // held must go too -- otherwise an invisible filter keeps narrowing the list.
    clears: ["agent"],
    display: (v) => (v ? "Assigned" : "Unassigned"),
  },
  {
    key: "agent",
    label: "Agent",
    kind: "object",
    group: "Processing",
    display: (v) => v.name,
    param: (v) => ({ agent_search_query: v.name }),
  },
  {
    key: "document_type",
    label: "Doc type",
    kind: "scalar",
    param: "document_type",
    group: "Processing",
    // The document type decides which statuses are even possible, so a stale
    // status from the previous type would filter on a value that cannot occur.
    clears: ["document_request_status"],
  },
  {
    key: "document_request_status",
    label: "Doc status",
    kind: "multi",
    param: "document_request_status",
    group: "Processing",
  },
  {
    key: "perfios_status",
    label: "Perfios",
    kind: "multi",
    param: "perfios_status",
    group: "Processing",
  },

  /* ----------------------------------------------------------- attribution */
  /* Where the lead came from -- the same six User Management already has.

     Note the `[]` on every param name, which is NOT a typo and NOT what
     USER_FILTERS uses. Three conventions are live in this codebase:

       customer_list/            POST body, plain `utm_source` arrays
       precheck_details_fast/    GET, `utm_source[]` repeated
       everything else on GET    plain repeated keys, e.g. `city=A&city=B`

     serializeParams() writes whatever key it is given verbatim (deliberately,
     see its docstring), so `param: "utm_source"` here would emit `utm_source=A`
     and the server's getlist("utm_source[]") would read nothing -- the request
     would succeed, the table would not narrow, and nothing would report an
     error. Spelled this way the filters reach the list, the stage counts and
     the CSV export, all of which share _precheck_list_lightweight(). */
  { key: "utm_source", label: "Partner ID (utm_source)", kind: "multi", param: "utm_source[]", group: "Attribution" },
  { key: "utm_medium", label: "Publisher (utm_medium)", kind: "multi", param: "utm_medium[]", group: "Attribution" },
  {
    key: "utm_campaign",
    label: "UTM Campaign",
    kind: "multi",
    param: "utm_campaign[]",
    group: "Attribution",
  },
  {
    key: "utm_content",
    label: "Source (utm_content)",
    kind: "multi",
    param: "utm_content[]",
    group: "Attribution",
  },
  { key: "utm_term", label: "UTM Term", kind: "multi", param: "utm_term[]", group: "Attribution" },
  {
    key: "utm_platform",
    label: "UTM Platform",
    kind: "multi",
    param: "utm_platform[]",
    group: "Attribution",
  },
];

/**
 * User Management. Same contract, different screen.
 *
 * The three band filters carry a `param` function because one choice sets two
 * request values (a min and a max) -- the same shape `availed_offer` uses above.
 */
export const USER_FILTERS = [
  { key: "city", label: "City", kind: "multi", param: "city", group: "Customer" },
  { key: "state", label: "State", kind: "multi", param: "state", group: "Customer" },
  { key: "platform", label: "Platform", kind: "multi", param: "platform", group: "Customer" },
  {
    key: "lead_partner",
    label: "Lead source",
    kind: "multi",
    param: "lead_partner",
    group: "Customer",
  },
  {
    key: "age_band",
    label: "Age",
    kind: "object",
    group: "Profile",
    display: (v) => v.name,
    param: (v) => ({ age_min: v.min_value, age_max: v.max_value }),
  },
  {
    key: "salary_band",
    label: "Salary",
    kind: "object",
    group: "Profile",
    display: (v) => v.name,
    param: (v) => ({ income_min: v.min_value, income_max: v.max_value }),
  },
  {
    key: "activity_band",
    label: "Last active",
    kind: "object",
    group: "Profile",
    display: (v) => v.name,
    // Days ago rather than a pair of dates: the server subtracts from its own
    // clock, so "last 7 days" cannot mean something different for a browser in
    // another timezone. Note this is NOT the date range on the bar above --
    // that one filters on when the customer signed up.
    param: (v) => ({ active_days_min: v.min_value, active_days_max: v.max_value }),
  },

  /* ----------------------------------------------------------- attribution */
  /* Where the lead came from. The old admin's customer screen has had these six
     since UTM tracking was added and this screen never got them, so an operator
     who wanted "everyone from the September Google campaign" had to go back to
     the legacy screen for it.
   
     Plain `multi`, like City and State, because the server takes them exactly
     that way: customer_list/ reads utm_source ... utm_platform from the POST
     body as arrays (webpanel/views.py). Note that is NOT the `utm_source[]`
     spelling the older GET endpoints use, and NOT `placement` -- which is what
     the legacy screen sends for UTM Platform, and why its UTM Platform filter
     has never actually filtered anything. */
  { key: "utm_source", label: "Partner ID (utm_source)", kind: "multi", param: "utm_source", group: "Attribution" },
  { key: "utm_medium", label: "Publisher (utm_medium)", kind: "multi", param: "utm_medium", group: "Attribution" },
  {
    key: "utm_campaign",
    label: "UTM Campaign",
    kind: "multi",
    param: "utm_campaign",
    group: "Attribution",
  },
  {
    key: "utm_content",
    label: "Source (utm_content)",
    kind: "multi",
    param: "utm_content",
    group: "Attribution",
  },
  { key: "utm_term", label: "UTM Term", kind: "multi", param: "utm_term", group: "Attribution" },
  {
    key: "utm_platform",
    label: "UTM Platform",
    kind: "multi",
    param: "utm_platform",
    group: "Attribution",
  },

  /* ---------------------------------------------------------- verification */
  /* The last three the legacy New Customer screen had and this one did not.
     They were dead there -- sent on every request, read by nothing -- so these
     are new behaviour rather than a port; see get_filtered_customer_list. */
  {
    key: "kyc_step",
    label: "KYC Document",
    kind: "scalar",
    param: "kyc_step",
    group: "Verification",
    // The document decides nothing about which statuses exist -- all three
    // carry the same four -- but a status left behind from the previous
    // document would keep narrowing the table under a control the operator
    // thinks they have changed. Same reasoning as document_type above.
    clears: ["kyc_status"],
  },
  {
    key: "kyc_status",
    label: "KYC Status",
    kind: "scalar",
    param: "kyc_status",
    group: "Verification",
  },
  {
    key: "availed_offer",
    label: "Availed offers",
    kind: "object",
    group: "Verification",
    display: (v) => v.name,
    param: (v) => ({
      availed_offer_count_min: v.min_value,
      availed_offer_count_max: v.max_value,
    }),
  },
];

/**
 * Products.
 *
 * The band filter carries a `param` function because one choice sets two
 * request values.
 *
 * Tenure and loan-up-to bands used to live here. They went when their columns
 * moved to the product detail page: a filter for a column the table no longer
 * shows would still have been sending tenure_min / loan_upto_max on every load.
 * The server still accepts those params and Team -> Filtered view can still
 * lock a member to a band (team/view_filters.py reads its own spec, not this
 * one), so they can come back by re-adding the two entries.
 *
 * Interest rate was never here: the column mixes per-annum and per-month rates,
 * so a numeric filter over it would rank 1.5% p.m. below 10.5% p.a. and hide
 * the wrong products.
 */
export const PRODUCT_FILTERS = [
  {
    key: "priority_band",
    label: "Priority",
    kind: "object",
    group: "Placement",
    display: (v) => v.name,
    param: (v) => ({ priority_min: v.min_value, priority_max: v.max_value }),
  },
  {
    key: "recommended",
    label: "Recommended",
    kind: "bool",
    param: "flag_recomended",
    group: "Placement",
    display: (v) => (v ? "Yes" : "No"),
  },
];

/** What "not filtering" looks like, per kind. */
const EMPTY = {
  multi: () => [],
  range: () => [],
  date: () => null,
  bool: () => null,
  scalar: () => null,
  object: () => null,
};

function emptyFor(spec) {
  return EMPTY[spec.kind]();
}

/**
 * Every helper below takes the spec it operates on, so a second screen gets the
 * same defaults / params / chips / reset machinery by declaring an array rather
 * than by copying any of this.
 */

/** A fresh, fully-populated filter object. The only place defaults live. */
export function defaultFilters(spec = APPLICATION_FILTERS) {
  const out = {};
  spec.forEach((f) => {
    out[f.key] = emptyFor(f);
  });
  return out;
}

function isSet(spec, value) {
  if (value === null || value === undefined || value === "") return false;
  if (spec.kind === "multi" || spec.kind === "range") return value.length > 0;
  return true;
}

/** Reset one filter, plus anything that depends on it. */
export function clearOne(filters, key, all = APPLICATION_FILTERS) {
  const spec = all.find((f) => f.key === key);
  if (!spec) return filters;
  const next = { ...filters, [key]: emptyFor(spec) };
  (spec.clears || []).forEach((k) => {
    const dep = all.find((f) => f.key === k);
    if (dep) next[k] = emptyFor(dep);
  });
  return next;
}

/** Request params for whatever is actually set. Unset filters send nothing. */
export function toParams(filters, spec = APPLICATION_FILTERS) {
  const out = {};
  spec.forEach((f) => {
    const value = filters[f.key];
    if (!isSet(f, value)) return;
    if (typeof f.param === "function") Object.assign(out, f.param(value));
    else out[f.param] = value;
  });
  return out;
}

/** One entry per active filter, for the removable chip strip. */
export function activeFilters(filters, spec = APPLICATION_FILTERS) {
  return spec.filter((f) => isSet(f, filters[f.key])).map((f) => {
    const value = filters[f.key];
    let text;
    if (f.display) text = f.display(value);
    else if (f.kind === "multi") text = value.length + " selected";
    else if (f.kind === "range") text = fmt.date(value[0]) + " → " + fmt.date(value[1]);
    else text = String(value);
    return { key: f.key, label: f.label, value: text };
  });
}

export default {
  APPLICATION_FILTERS,
  USER_FILTERS,
  PRODUCT_FILTERS,
  defaultFilters,
  clearOne,
  toParams,
  activeFilters,
};
